import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Factory, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

export const Services = () => {
  const { t } = useTranslation();

  const iconMap = {
    1: Factory,
    2: Zap
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO {...SEO_CONTENT.services} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              {t('services.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              {t('services.completeSolutions')}
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.completeSolutionsDesc')}
            </p>
          </div>

          <div className="space-y-12 md:space-y-20">
            {services.map((service, index) => {
              const Icon = iconMap[service.id];
              const isEven = index % 2 === 0;

              return (
                <div key={service.id} className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
                  <div className={`${isEven ? 'md:order-1' : 'md:order-2'}`}>
                    <div className="relative">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="rounded-lg shadow-2xl w-full h-64 md:h-96 object-cover"
                      />
                      <div className="absolute top-4 left-4 md:top-6 md:left-6 bg-white p-3 md:p-4 rounded-lg shadow-xl">
                        <Icon className="text-orange-600" size={24} />
                      </div>
                    </div>
                  </div>

                  <div className={`${isEven ? 'md:order-2' : 'md:order-1'}`}>
                    <div className="inline-block px-3 py-1 md:px-4 bg-orange-100 text-orange-600 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-4">
                      {service.subtitle}
                    </div>
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                      {service.title}
                    </h3>
                    <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="mb-4 md:mb-6">
                      <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">{t('services.keyBenefits')}</h4>
                      <div className="grid gap-2 md:gap-3">
                        {service.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start space-x-2 md:space-x-3">
                            <CheckCircle2 size={18} className="text-teal-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm md:text-base text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Card className="bg-gray-50 border-l-4 border-l-orange-600">
                      <CardContent className="pt-4 md:pt-6">
                        <h4 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{t('services.idealFor')}</h4>
                        <p className="text-gray-600 text-sm md:text-base">{service.forWhom}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach - CRDMO Model */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              {t('services.crdmoModelTitle')}
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.crdmoModelDesc')}
            </p>
            <p className="text-sm md:text-base text-teal-700 max-w-3xl mx-auto mt-3 font-medium">
              Our CRDMO model is enhanced by AI-driven route design, automated patent screening, and computational experiment optimization, reducing development timelines by up to 50%.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            <Card className="border-t-4 border-t-teal-600 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6 md:pt-8 text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <span className="text-2xl md:text-3xl font-bold text-teal-600">1</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('services.processDev')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('services.processDevDesc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-orange-600 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6 md:pt-8 text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <span className="text-2xl md:text-3xl font-bold text-orange-600">2</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('services.scaleUp')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('services.scaleUpDesc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-teal-600 hover:shadow-xl transition-shadow duration-300 sm:col-span-2 md:col-span-1">
              <CardContent className="pt-6 md:pt-8 text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <span className="text-2xl md:text-3xl font-bold text-teal-600">3</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('services.supplyChain')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('services.supplyChainDesc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
            {t('services.readyToScale')}
          </h2>
          <p className="text-base md:text-xl text-gray-300 mb-6 md:mb-8">
            {t('services.readyToScaleDesc')}
          </p>
          <Link to="/get-in-touch">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg">
              {t('services.getInTouch')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
